<template>
  <el-dialog
    id="edit-modal"
    class="form-modal"
    :visible.sync="editVisible"
    v-loading="editProcessing"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="edit-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">View / Edit Call Queue</h1>
        <div class="modal-actions col-2">
          <el-button
            type="primary"
            :disabled="numUsed"
            @click="presentDestsModal"
            >Save</el-button
          >
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="cancelOperation"
          >
            Cancel
          </el-button>
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="cancelOperation"
          ></el-button>
        </div>
      </div>
    </div>

    <el-form>
      <recent-audits
        service="Call Queue"
        :record="selectedQueue"
        v-if="!queueAdmin"
      />
      <el-tabs v-model="activeTabName">
        <el-tab-pane v-if="!queueAdmin" label="General" name="general">
          <el-checkbox v-model="editForm.recording"
            >Enable recording</el-checkbox
          >
          <div class="inline-form-group">
            <el-col :span="11">
              <el-input v-model="editForm.name" placeholder="Name">
                <template slot="prefix">Name</template>
              </el-input>
            </el-col>
            <el-col :span="1"></el-col>
            <el-col :span="12">
              <el-input v-model="editForm.number" placeholder="Number">
                <template slot="prefix">Number</template>
                <template slot="append" v-if="numUsed">
                  <in-use :inUse="numInUse" />
                </template>
              </el-input>
            </el-col>
          </div>
          <div class="inline-form-group">
            <el-col :span="11">
              <el-select
                v-model="editForm.ringStrategy"
                placeholder="Select ring strategy"
              >
                <el-option
                  v-for="item in ringStrategyOpts"
                  :key="item.label"
                  :label="item.label"
                  :value="item.value"
                />
                <template slot="prefix">Ring strategy</template>
              </el-select>
            </el-col>
            <el-col :span="1" />
            <el-col :span="12">
              <el-input
                v-model="editForm.timeout"
                min="1"
                placeholder="Enter timeout"
              >
                <template slot="prefix">Timeout (seconds)</template>
              </el-input>
            </el-col>
          </div>

          <el-select placeholder="Select branch" v-model="editForm.branchId" filterable>
            <template slot="prefix"> Branch </template>
            <el-option
              v-for="item in branchOptions"
              :key="item.id"
              :value="item.id"
              :label="item.name"
            />
          </el-select>

          <el-select
            placeholder="Select max queue length"
            v-model="editForm.maxlen"
          >
            <el-option
              v-for="(num, i) in 10"
              :key="i"
              :label="num * 5"
              :value="num * 5"
            />
            <template slot="prefix">Max queue length</template>
          </el-select>
          <el-tag class="mb-10">
            <i class="el-icon-info" style="margin-right: 4px"></i>
            The maximum number of callers waiting in queue
          </el-tag>

          <h2 class="form-section-header">Timeout destinations</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="draggableDestTypes"
                :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
            :selectedDestinations="editForm.timeoutDestinations"
            @destinationsSelected="onTimeoutDestinationsSelection"
            title=""
            :clearFields="clearFormFields"
          />

          <h2 class="form-section-header">If queue is full</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="draggableDestTypes"
                :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
            :selectedDestinations="editForm.queueFullDestinations"
            @destinationsSelected="onQueueFullDestinationsSelection"
            title=""
            :clearFields="clearFormFields"
          />

          <h2 class="form-section-header">Conditional routing</h2>
          <div class="mt-5">
            <el-checkbox v-model="editForm.nobodyHome">
              Enable redirecting call if no agents are available
              <el-popover placement="top" trigger="hover">
                <template slot="reference">
                  <i class="el-icon-question"></i>
                </template>
                <el-tag type="primary">
                  <span
                    >Followed when a call cannot be handled by any agents</span
                  >
                </el-tag>
              </el-popover>
            </el-checkbox>
            <div v-if="editForm.nobodyHome">
              <h4 class="form-section-header">If no agents are available</h4>
              <destination-selector
                v-if="editForm.nobodyHome"
                :isTerminalDestSelector="true"
                :draggable="true"
                :showHeader="false"
                :draggableDestTypes="draggableDestTypes"
                :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
                :selectedDestinations="editForm.noAvailableAgentsDestinations"
                @destinationsSelected="noAvailableAgentsDestinationsSelection"
                title=""
                :clearFields="clearFormFields"
              />
            </div>

            <el-checkbox v-model="editForm.noFreeMember">
              Enable redirecting call if all agents busy
              <el-popover placement="top" trigger="hover">
                <template slot="reference">
                  <i class="el-icon-question"></i>
                </template>
                <el-tag type="primary">
                  <span
                    >Followed when agent cannot immediately service a call</span
                  >
                </el-tag>
              </el-popover>
            </el-checkbox>
            <div v-if="editForm.noFreeMember">
              <h4 class="form-section-header">If all agents are busy</h4>
              <destination-selector
                :isTerminalDestSelector="true"
                :draggable="true"
                :showHeader="false"
                :draggableDestTypes="draggableDestTypes"
                :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
                :selectedDestinations="editForm.allAgentsBusyDestinations"
                @destinationsSelected="allAgentsBusyDestinationsSelection"
                title=""
                :clearFields="clearFormFields"
              />
            </div>
          </div>

          <h2 class="form-section-header">Abandoned call notifications</h2>
          <div class="mt-5">
            <el-checkbox v-model="editForm.notifyAbandoned"
              >Enable abandoned call notifications</el-checkbox
            >
            <el-input
              v-if="editForm.notifyAbandoned"
              v-model="editForm.notifyAbandonedEmail"
              placeholder="Notify abandon email"
            >
              <template slot="prefix">Notify abandon email</template>
            </el-input>
          </div>

          <div v-if="editForm.dids.length">
            <h2 class="form-section-header">Assigned phone numbers</h2>
            <el-select
              v-model="editForm.dids"
              placeholder="Select numbers to assign"
              :disabled="true"
              multiple
            >
              <template slot="prefix"
                ><span style="opacity: 0">Numbers</span></template
              >
              <el-option
                v-for="(item, i) in selectedDids"
                :key="i"
                :label="item.number | formatNumber"
                :value="item.id"
              />
            </el-select>
            <el-tag class="mb-10">
              <i class="el-icon-info" style="margin-right: 4px"></i>
              You can modify this in the phone numbers section
            </el-tag>
          </div>
        </el-tab-pane>

        <el-tab-pane
          v-if="!queueAdmin"
          label="Exit and Callback Settings"
          name="exitAndCallback"
        >
          <el-checkbox v-model="editForm.callback">
            Enable Queue Callback
          </el-checkbox>

          <div v-if="editForm.callback">
            <h3 class="form-section-header">Queue callback settings</h3>
            <div class="inline-form-group">
              <el-col :span="11">
                <dads-select
                  placeholder="Caller id number"
                  v-model="editForm.callbackCidNum"
                  filterable
                  clearable
                  remote
                  :remote-method="remoteSearchForCidNums"
                >
                  <el-option
                    v-for="(item, i) in cidNumOptions"
                    :key="i"
                    :label="item.label | formatNumber"
                    :value="item.label"
                  />
                  <template slot="prefix">Caller id number</template>
                </dads-select>
              </el-col>
              <el-col :span="1" />
              <el-col :span="12">
                <el-select
                  placeholder="Select max attempts"
                  v-model="editForm.callbackMaxAttempts"
                >
                  <el-option
                    v-for="(n, i) in 5"
                    :key="i"
                    :label="n"
                    :value="n"
                  />
                  <template slot="prefix">Max attempts</template>
                </el-select>
              </el-col>
            </div>

            <existing-mediafile
              title="Play when connecting To agent"
              :multiple="false"
              :selectedMeId="editForm.agentConnectMeId"
              :clearState="clearFormFields"
              :newMediafile="newMediaFile"
              @mediafileSelected="editForm.agentConnectMeId = $event"
            />
            <new-mediafile @mediafileSaved="newMediaFile = $event" />
          </div>

          <el-checkbox v-model="queueExitKey">
            Enable Queue Exit Key
          </el-checkbox>

          <div v-if="queueExitKey">
            <h3 class="form-section-header">Exit key settings</h3>
            <el-select
              v-model="editForm.exitKey"
              placeholder="Select exit key"
              v-if="queueExitKey"
              clearable
            >
              <el-option
                v-for="(item, i) in exitKeyOptions"
                :key="i"
                :label="item"
                :value="item"
              />
              <template slot="prefix">Exit key</template>
            </el-select>

            <dads-select
              v-if="editForm.exitKey === 'Use virtual receptionist options'"
              placeholder="Select virtual receptionist"
              v-model="editForm.ivrId"
              clearable
              filterable
            >
              <el-option
                v-for="(item, i) in ivrOptions"
                :key="i"
                :label="item.name"
                :value="item.id"
              />

              <template slot="prefix">Virtual receptionist</template>
            </dads-select>

            <div v-else-if="editForm.exitKey !== ''">
              <h2 class="form-section-header">Exit Key Destinations</h2>
              <destination-selector
                :isTerminalDestSelector="true"
                :draggable="true"
                :showHeader="false"
                :draggableDestTypes="draggableDestTypes"
                :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
                :selectedDestinations="editForm.exitKeyDestinations"
                @destinationsSelected="onExitKeyDestinationsSelection"
                title=""
                :clearFields="clearFormFields"
              />
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane v-if="!queueAdmin" label="Media" name="media">
          <new-mediafile @mediafileSaved="newMediaFile = $event" />
          <el-select
            v-model="playToCaller"
            placeholder="Play to the caller while waiting in the queue"
          >
            <el-option
              v-for="(item, i) in playToCallerOpts"
              :key="i"
              :label="item.label"
              :value="item.value"
            />
            <template slot="prefix"
              >Play to the caller while waiting in the queue</template
            >
          </el-select>

          <h2 class="form-section-header">Periodic announcements</h2>
          <el-checkbox v-model="enablePeriodicAnnounce">
            Enable periodic announcements
          </el-checkbox>
          <el-select
            v-model="editForm.periodicAnnounceFrequency"
            v-if="enablePeriodicAnnounce"
            placeholder="Select frequency"
          >
            <div v-for="(n, i) in 120" :key="i">
              <el-option v-if="n !== 1" :label="n * 5" :value="n * 5" />
            </div>
            <template slot="prefix">Frequency</template>
          </el-select>
          <existing-mediafile
            v-if="enablePeriodicAnnounce"
            title="Periodic Announce Message"
            :multiple="false"
            :clearState="clearFormFields"
            :selectedMeId="editForm.periodicAnnounceMeId"
            :newMediafile="newMediaFile"
            :defaultMessage="true"
            @mediafileSelected="editForm.periodicAnnounceMeId = $event"
          />

          <h2 class="form-section-header">Hold time announcements</h2>
          <div class="mt-5">
            <el-checkbox v-model="editForm.reportHoldTime">
              Enable reporting caller's hold time to agent
            </el-checkbox>
            <div class="inline-form-group">
              <el-col :span="10">
                <el-select
                  v-model="editForm.announceHoldTime"
                  placeholder="Select announce hold time to user"
                >
                  <el-option
                    v-for="(item, i) in announceHoldTimeOptions"
                    :key="i"
                    :label="item.label"
                    :value="item.value"
                  />
                  <template slot="prefix">Announce hold time to user</template>
                </el-select>
              </el-col>
            </div>
            <div v-if="editForm.announceHoldTime !== 'no'">
              <existing-mediafile
                title="Est hold time message"
                :multiple="false"
                :clearState="clearFormFields"
                :selectedMeId="editForm.holdtimeMeId"
                :newMediafile="newMediaFile"
                :defaultMessage="true"
                @mediafileSelected="editForm.holdtimeMeId = $event"
              />
            </div>
          </div>

          <h2 class="form-section-header">Position announcements</h2>
          <div class="inline-form-group">
            <el-col :span="10">
              <el-select
                v-model="editForm.announcePosition"
                placeholder="Announce position To user"
              >
                <el-option
                  v-for="(item, i) in announcePositionOptions"
                  :key="i"
                  :label="item.label"
                  :value="item.value"
                />
                <template slot="prefix">Announce position to user</template>
              </el-select>
            </el-col>
            <el-col :span="1" />
            <el-col :span="13">
              <el-input
                v-if="
                  editForm.announcePosition === 'more' ||
                  editForm.announcePosition === 'limit'
                "
                v-model="editForm.announcePositionLimit"
                placeholder="Enter limit"
                min="1"
              >
                <template slot="prefix">Limit</template>
              </el-input>
            </el-col>
          </div>

          <div v-if="editForm.announcePosition !== 'no'">
            <existing-mediafile
              title="You are the next message"
              :multiple="false"
              :clearState="clearFormFields"
              :newMediafile="newMediaFile"
              :defaultMessage="true"
              :selectedMeId="editForm.youAreNextMeId"
              @mediafileSelected="editForm.youAreNextMeId = $event"
            />

            <existing-mediafile
              title="There are message"
              :multiple="false"
              :clearState="clearFormFields"
              :newMediafile="newMediaFile"
              :defaultMessage="true"
              :selectedMeId="editForm.thereAreMeId"
              @mediafileSelected="editForm.thereAreMeId = $event"
            />

            <existing-mediafile
              title="Calls waiting message"
              :multiple="false"
              :clearState="clearFormFields"
              :newMediafile="newMediaFile"
              :defaultMessage="true"
              :selectedMeId="editForm.callsWaitingMeId"
              @mediafileSelected="editForm.callsWaitingMeId = $event"
            />

            <existing-mediafile
              title="Thank you message"
              :multiple="false"
              :clearState="clearFormFields"
              :newMediafile="newMediaFile"
              :selectedMeId="editForm.thankYouMeId"
              :defaultMessage="true"
              @mediafileSelected="editForm.thankYouMeId = $event"
            />
          </div>

          <div
            v-if="
              editForm.announceHoldTime !== 'no' ||
              editForm.announcePosition !== 'no'
            "
          >
            <h2 class="form-section-header">
              Hold and position announcement frequency
            </h2>
            <el-col :span="10">
              <el-select
                v-model="editForm.announceFrequency"
                placeholder="Announce frequency"
              >
                <div v-for="(n, i) in 12" :key="i">
                  <el-option v-if="n !== 1" :label="n * 10" :value="n * 10" />
                </div>
                <template slot="prefix">Announce frequency</template>
              </el-select>
            </el-col>
          </div>
        </el-tab-pane>

        <el-tab-pane label="Agent Settings" name="agent">
          <div v-if="!queueAdmin">
            <el-checkbox v-model="editForm.ringInUse">
              Enable ringing agent with in use status
            </el-checkbox>
            <el-checkbox v-model="editForm.autoPause">
              Enable auto pause
            </el-checkbox>
            <el-checkbox v-model="editForm.autologout">
              Enable auto logout
            </el-checkbox>
            <el-checkbox v-model="ringAgentsOnlyOnce">
              Enable ringing agents only once
            </el-checkbox>
            <el-checkbox v-model="editForm.autofill">
              Enable auto fill
            </el-checkbox>
            <el-checkbox v-model="editForm.agentPickup">
              Enable agent call pickup
            </el-checkbox>
            <div class="inline-form-group">
              <el-col :span="10">
                <div class="flex align-center">
                  <el-checkbox v-model="tierTimeoutEnabled">
                    Enable tier timeout rule
                  </el-checkbox>
                  <el-popover placement="top" trigger="hover">
                    <template slot="reference">
                      <i class="el-icon-question ml-5"></i>
                    </template>
                    <span class="black-text max-w-200">
                    For ring all queues agents are called in combined tier groups per timeout
                    <br>
                    Example: Tier 1 rings -> Timeout -> Tier 1 and 2 rings -> Timeout -> Tier 1, 2, and 3 rings.
                    <br>
                    This setting does not override agent ring duration - it is recommended to keep ring duration at or below the tier threshold
                  </span>
                  </el-popover>
                </div>
              </el-col>
              <el-col :span="1"></el-col>
              <el-col :span="13" v-if="tierTimeoutEnabled">
                <el-select
                  v-model="editForm.tierTimeout"
                  placeholder="Select timeout"
                >
                  <el-option
                    label="5 seconds"
                    value="5"
                  />
                  <el-option
                    label="10 seconds"
                    value="10"
                  />
                  <el-option
                    label="15 seconds"
                    value="15"
                  />
                  <el-option
                    label="30 seconds"
                    value="30"
                  />
                  <el-option
                    label="60 seconds"
                    value="60"
                  />
                  <template slot="prefix">Timeout (s)</template>
                </el-select>
              </el-col>
            </div>

            <div class="inline-form-group">
              <el-col :span="10">
                <el-select
                  v-model="editForm.agentTimeout"
                  placeholder="Agent timeout"
                >
                  <el-option
                    v-for="(n, i) in 19"
                    :key="i"
                    :label="n + 1"
                    :value="(n + 1) * 5"
                  />
                  <template slot="prefix">Agent timeout (rings)</template>
                </el-select>
              </el-col>
              <el-col :span="1"></el-col>
              <el-col :span="13">
                <el-input
                  v-model="editForm.agentWrapupTime"
                  min="0"
                  type="number"
                  placeholder="Agent wrap up time"
                >
                  <template slot="prefix">Agent wrap up time</template>
                </el-input>
              </el-col>
            </div>
          </div>

          <h2 class="form-section-header">Assign allowed agents</h2>
          <draggable
            :key="draggableKey"
            @change="changeDraggableKey"
            v-model="editForm.allowedAgents"
          >
            <transition-group name="list" tag="div">
              <div
                class="inline-form-group"
                v-for="(item, i) in editForm.allowedAgents"
                :key="i + '$ad982783c(-'"
              >
                <el-col :span="15">
                  <dads-select
                    :placeholder="`Select Agent ${i + 1}`"
                    :remote-method="(query) => remoteSearchExtensions(query, i)"
                    v-model="item.id"
                    remote
                    filterable
                    clearable
                    @change="($event) => onAllowedAgentSelection($event, i)"
                  >
                    <el-option
                      v-for="(el, j) in item.options"
                      :key="j"
                      :label="el.name + ' - ' + el.number"
                      :value="el.id"
                    />
                    <template slot="prefix">Agent</template>
                  </dads-select>
                </el-col>
                <el-col :span="1" />
                <el-col :span="3">
                  <el-select
                    v-if="tierTimeoutEnabled"
                    class="ml-5"
                    placeholder="Tier"
                    v-model="item.penalty"
                    @change="($event) => onAgentTierSelect($event, item.id, i)"
                  >
                    <el-option
                      label="1"
                      value="1"
                    />
                    <el-option
                      label="2"
                      value="2"
                    />
                    <el-option
                      label="3"
                      value="3"
                    />
                    <el-option
                      label="4"
                      value="4"
                    />
                    <el-option
                      label="5"
                      value="5"
                    />
                    <template slot="prefix">Tier</template>
                  </el-select>
                </el-col>
                <el-col :span="5" class="text-right">
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="`Remove Agent ${i + 1}`"
                    placement="top"
                  >
                    <el-button
                      class="ml-5"
                      style="float: right; margin-right: 1px"
                      @click="removeAllowedAgent(i)"
                      icon="el-icon-delete"
                    />
                  </el-tooltip>
                  <el-button icon="el-icon-rank" type="default" />
                </el-col>
              </div>
            </transition-group>
          </draggable>

          <el-button
            type="text"
            icon="el-icon-circle-plus-outline"
            :disabled="disabledToAddAgent"
            @click="addAllowedAgent"
          >
            Add Agent
          </el-button>
        </el-tab-pane>
      </el-tabs>

      <el-dialog
        class="form-modal"
        :visible.sync="destsModalVisible"
        :show-close="false"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :modal="false"
      >
        <h2 class="form-section-header">Update Agent Extensions</h2>
        <div class="inline-form-group">
          <div class="black-text">
            <p>For any selected agents update the following:</p>
            <ol>
              <li>
                Update extension branch to match the branch assigned to this
                queue.
              </li>
              <li>
                Update busy, no answer, and offline destinations to point to the
                selected destination
              </li>
            </ol>
          </div>
        </div>

        <el-select
          v-model="editForm.destUpdateExts"
          placeholder="Select Agents"
          multiple
        >
          <template v-for="item in editForm.allowedAgents">
            <el-option
              v-if="item.value"
              :key="item.id"
              :label="item.value.name + '-' + item.value.number"
              :value="item.id"
            />
          </template>

          <template slot="prefix">
            <span style="opacity: 0">Agents</span>
          </template>
        </el-select>

        <div v-if="editForm.destUpdateExts.length">
          <h2 class="form-section-header">Extension Destinations</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="['CONDITION', 'QUEUE']"
            :selectedDestinations="editForm.extDestinations"
            @destinationsSelected="onExtDestinationsSelect"
            title=""
            :clearFields="clearFormFields"
          />
        </div>

        <div class="inline-form-group">
          <el-button @click="destsModalVisible = false"> Cancel </el-button>
          <el-button @click="editOperation">
            {{ editForm.destUpdateExts.length ? "Submit" : "Skip" }}
          </el-button>
        </div>
      </el-dialog>
    </el-form>

    <destination-references
      v-if="!queueAdmin"
      :tenantId="selectedQueue ? selectedQueue.tenantId : null"
      type="QUEUE"
      :id="selectedQueue ? selectedQueue.id : null"
      :clearState="clearFormFields"
    />
  </el-dialog>
</template>

<script>
import DestinationSelector from "@/components/DestinationSelector.vue";
import ExistingMediafile from "@/components/ExistingMediafile";
import NewMediafile from "@/components/NewMediafile";
import feathersClient from "@/feathers/feathers-client";
import Draggable from "vuedraggable";
import DadsSelect from "@/components/select";
import DestinationReferences from "@/components/DestinationReferences";
import RecentAudits from "@/components/RecentAudits";

import InUse from "@/components/InUse";

export default {
  name: "EditCallQueuesForm",

  props: {
    editVisible: { required: true },
    clearFormFields: { required: true },
    editProcessing: { required: true },
    ivrOptions: { required: true },
    cidNumOptions: { required: true },
    numInUse: { required: true },
    selectedQueue: { required: true },
    draggableDestTypes: { required: true },
    branchOptions: { required: true },
  },

  components: {
    DestinationSelector,
    ExistingMediafile,
    NewMediafile,
    Draggable,
    DadsSelect,
    DestinationReferences,
    RecentAudits,
    InUse,
  },

  data() {
    return {
      newMediaFile: null,
      periodicAnnounceNewMediafile: null,
      holdTimeNewMediafile: null,
      defaultMsgNewMediafile: null,
      agentConnectNewMediafile: null,
      draggableKey: "",
      allowedQueueMembers: [],
      activeTabName: "general",
      initialNumber: null,
      queueExitKey: false,
      destsModalVisible: false,
      selectedDids: [],
      exitKeyOptions: [
        "0",
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "Use virtual receptionist options",
      ],
      ringStrategyOpts: [
        { label: "Fewest calls", value: "FEWESTCALLS" },
        // { label: "Least recent", value: "LEASTRECENT" },
        { label: "Linear", value: "LINEAR" },
        { label: "Random", value: "RANDOM" },
        { label: "Ring all", value: "RINGALL" },
        { label: "Round robin", value: "RRMEMORY" },
      ],
      playToCallerOpts: [
        { label: "Music on hold", value: "" },
        { label: "Ringing", value: "r" },
        { label: "Music on hold, then ringing", value: "R" },
      ],
      playToCaller: null,
      ringAgentsOnlyOnce: false,
      announceHoldTimeOptions: [
        { label: "No", value: "no" },
        { label: "Yes", value: "yes" },
        { label: "Once", value: "once" },
      ],
      announcePositionOptions: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
        { label: "If less than", value: "limit" },
        { label: "No more than", value: "more" },
      ],
      enablePeriodicAnnounce: false,
      tierTimeoutEnabled: false,
      editForm: {
        tenantId: null,
        tenantCode: "",

        // ⭐️ General Tab ⭐️ //
        name: "",
        number: "",
        recording: false,
        ringStrategy: "",
        timeout: 120,
        timeoutDestinations: [],
        maxlen: "",
        queueFullDestinations: [],
        nobodyHome: false,
        noAvailableAgentsDestinations: [],
        noFreeMember: false,
        allAgentsBusyDestinations: [],
        notifyAbandoned: false,
        notifyAbandonedEmail: "",
        dids: [],
        branchId: null,

        // ⭐️ Exit and Callback Settings ⭐️ //
        callback: false,
        callbackCidNum: "",
        callbackMaxAttempts: 1,
        agentConnectMeId: null,
        exitKey: "",
        exitKeyDestinations: [],
        ivrId: null,

        // ⭐️ Media ⭐️ //
        parameters: "",
        reportHoldTime: false,
        periodicAnnounceFrequency: 10,
        periodicAnnounceMeId: null,
        announceHoldTime: "no",
        holdtimeMeId: null,
        announcePosition: "yes",
        announcePositionLimit: null,
        youAreNextMeId: null,
        thereAreMeId: null,
        callsWaitingMeId: null,
        thankYouMeId: null,
        announceFrequency: 0,

        // ⭐️ Agent Settings ⭐️ //
        ringInUse: false,
        autoPause: false,
        autologout: false,
        agentTimeout: 30,
        agentWrapupTime: 0,
        autofill: false,
        allowedAgents: [
          {
            id: null,
            options: [],
            value: null,
          },
        ],
        loggedInAgents: [
          {
            _interface: null,
          },
        ],
        destUpdateExts: [],
        extDestinations: [],
        enableAgentDestinations: false,
        agentPickup: false,
        tierTimeout: null,
      },
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    user() {
      return this.$store.state.auth.user;
    },

    queueAdmin() {
      if (this.user) {
        return +this.user.userRole === 7;
      }
      return false;
    },

    number() {
      return this.editForm.number;
    },

    disabledToAddAgent() {
      if (this.editForm.allowedAgents.length) {
        const lastIdx = this.editForm.allowedAgents.length - 1;
        return !this.editForm.allowedAgents[lastIdx].id;
      }
    },

    isAllowedToAddLoggedInAgent() {
      if (this.editForm.loggedInAgents.length) {
        const lastIdx = this.editForm.loggedInAgents.length - 1;
        return !this.editForm.loggedInAgents[lastIdx]._interface;
      }
    },

    announceHoldTime() {
      return this.editForm.announceHoldTime;
    },

    numUsed() {
      return this.numInUse && this.numInUse.inUse;
    },
  },

  watch: {
    clearFormFields(value) {
      if (value) {
        this.clearFields();
      }
    },

    editVisible() {
      this.changeDraggableKey();
      if (this.queueAdmin) {
        this.activeTabName = "agent";
      }
    },

    number(val) {
      if (val) {
        if (this.initialNumber !== val) {
          this.$emit("checkAvailableNum", val);
        }
      }
    },

    announceHoldTime(val) {
      if (val === "no") {
        this.editForm.holdtimeMeId = 0;
        this.holdTimeNewMediafile = null;
      }
    },

    tierTimeoutEnabled(){
      this.editForm.allowedAgents = [...this.editForm.allowedAgents].map((agent => {
        const penalty = this.tierTimeoutEnabled
          ? (agent.penalty ? agent.penalty : 1)
          : null;
        return {
          ...agent,
          penalty
        }
      }));
    },

    selectedQueue(val) {
      if (val) {
        feathersClient
          .service("queuemembers-allowed")
          .find({
            query: {
              queueName: val.id,
            },
          })
          .then((res) => {
            this.allowedQueueMembers = [...res];
          });

        // ⭐️ Map to Data to Edit Form ⭐️ //
        const {
          name,
          number,
          recording,
          timeout,
          nobodyHome,
          noFreeMember,
          notifyAbandoned,
          notifyAbandonedEmail,
          callback,
          callbackCidNum,
          callbackMaxAttempts,
          de_destinations,
          parameters,
          ivrId,
          periodicAnnounceMeId,
          youAreNextMeId,
          thereAreMeId,
          callsWaitingMeId,
          thankYouMeId,
          allowedAgents,
          loggedInAgents,
          dids,
          timeoutDestinations,
          queueFullDestinations,
          noAvailableAgentsDestinations,
          allAgentsBusyDestinations,
          exitKeyDestinations,
          holdtimeMeId,
          branchId,
          enableAgentDestinations,
          autologout,
          agentPickup,
          tierTimeout
        } = val;

        this.editForm.enableAgentDestinations = !!enableAgentDestinations;
        this.editForm.agentPickup = !!agentPickup;

        const {
          ringStrategy,
          maxlen,
          ringInUse,
          autoPause,
          agentTimeout,
          agentWrapupTime,
          announceFrequency,
          announceHoldTime,
          announcePosition,
          announcePositionLimit,
          reportHoldTime,
          context,
          periodicAnnounceFrequency,
          autofill
        } = val.queueConfig;

        this.editForm.timeoutDestinations = [...timeoutDestinations];
        this.editForm.queueFullDestinations = [...queueFullDestinations];
        this.editForm.branchId = branchId;
        this.editForm.noAvailableAgentsDestinations = [
          ...noAvailableAgentsDestinations,
        ];
        this.editForm.allAgentsBusyDestinations = [
          ...allAgentsBusyDestinations,
        ];
        this.editForm.exitKeyDestinations = [...exitKeyDestinations];

        if (dids && dids.length) {
          feathersClient
            .service("dids")
            .find({
              query: {
                id: { $in: dids },
                tenantId: this.selectedTenant.id,
              },
            })
            .then((res) => {
              this.selectedDids = [...res];
            });
          this.editForm.dids = [...dids];
        }
        this.editForm.name = name;
        this.editForm.number = number;
        this.initialNumber = number;
        this.editForm.recording = recording === "yes";
        this.editForm.ringStrategy = ringStrategy;
        this.editForm.timeout = timeout;
        this.editForm.maxlen = maxlen;
        this.editForm.nobodyHome = nobodyHome === "on";
        this.editForm.noFreeMember = noFreeMember === "on";
        this.editForm.notifyAbandoned = notifyAbandoned === "on";
        this.editForm.notifyAbandonedEmail = notifyAbandonedEmail;
        this.editForm.callback = callback === "on";
        this.editForm.callbackCidNum = callbackCidNum;
        this.editForm.callbackMaxAttempts = callbackMaxAttempts;
        this.editForm.ringInUse = ringInUse === "yes";
        this.editForm.autoPause = autoPause === "yes";
        this.editForm.autologout = autologout === 1;
        this.editForm.autofill = autofill === "yes";
        this.tierTimeoutEnabled = !!tierTimeout;
        this.editForm.tierTimeout = tierTimeout;

        //set periodic announce to default and it will override if found in destinations
        this.editForm.periodicAnnounceMeId = periodicAnnounceMeId;

        if (de_destinations && de_destinations.length) {
          de_destinations.forEach(({ typeSrc, typeDst, typeIdDst }) => {
            if (typeSrc === "QUEUE-ONCALLBACK" && typeDst === "PLAYBACK") {
              this.editForm.agentConnectMeId = typeIdDst;
            } else if (typeSrc === "QUEUE-PERIODICANNOUNCE") {
              this.editForm.periodicAnnounceMeId = typeIdDst;
            } else if (typeSrc === "QUEUE-EXITKEY") {
              this.queueExitKey = true;

              if (context && context.length) {
                this.editForm.exitKey = context[context.length - 1];
              }
            }
          });
        }

        //is the exit keys IVR options??
        this.editForm.ivrId = ivrId === 0 ? null : ivrId;
        if (this.editForm.ivrId && context.length) {
          if (context.split("_")[1] === "IVR") {
            this.editForm.exitKey = "Use virtual receptionist options";
            this.queueExitKey = true;
          }
        }

        if (parameters && parameters.length) {
          const parametersSplitArr = parameters.split("t");
          this.ringAgentsOnlyOnce = parametersSplitArr[0] === "n";
          this.playToCaller = parametersSplitArr[1];
        }

        this.editForm.agentTimeout = agentTimeout;
        this.editForm.agentWrapupTime = agentWrapupTime;
        this.editForm.announceFrequency = announceFrequency;
        this.editForm.announceHoldTime = announceHoldTime;
        this.editForm.announcePosition = announcePosition;
        this.editForm.announcePositionLimit = announcePositionLimit;
        this.editForm.reportHoldTime = reportHoldTime === "yes";

        this.editForm.youAreNextMeId = youAreNextMeId;
        this.editForm.thereAreMeId = thereAreMeId;
        this.editForm.callsWaitingMeId = callsWaitingMeId;
        this.editForm.thankYouMeId = thankYouMeId;
        this.editForm.holdtimeMeId = holdtimeMeId;
        this.editForm.periodicAnnounceFrequency = periodicAnnounceFrequency || 10;

        this.enablePeriodicAnnounce = periodicAnnounceFrequency > 0;

        if (allowedAgents.length) {
          this.editForm.allowedAgents = [...allowedAgents];
        }

        if (loggedInAgents.length) {
          this.editForm.loggedInAgents = [...loggedInAgents];
        }
      }
    },
  },

  methods: {
    changeDraggableKey() {
      const randomString = () => Math.random().toString(36).slice(2);
      this.draggableKey = `${randomString()}-${randomString()}$${randomString()}`;
    },

    onLoggedInAgentSelection(agent_interface, idx) {
      const selectedAgent = this.allowedQueueMembers.find(
        (q) => q.interface === agent_interface
      );

      const { membername, queueName, stateInterface, memberdevice, order } =
        selectedAgent;

      this.editForm.loggedInAgents[idx] = {
        membername,
        queueName,
        _interface: agent_interface,
        stateInterface,
        memberdevice,
        penalty: 0,
        paused: 0,
        loggedIn: 1,
        order,
      };

      this.editForm.loggedInAgents = this.editForm.loggedInAgents.sort(
        (a, z) => a.order - z.order
      );
    },

    onAllowedAgentSelection(agentId, i) {
      const agent = this.editForm.allowedAgents[i].options.find(
        (ag) => ag.id === agentId
      );

      this.editForm.allowedAgents[i].value = {
        id: agentId,
        name: agent.name,
        number: agent.number,
        penalty: this.tierTimeoutEnabled ? 1 : 0
      };
    },

    onAgentTierSelect(value, agentId, i) {
      if (!agentId) {
        this.editForm.allowedAgents[i].value = null;
        return;
      }

      const agent = this.editForm.allowedAgents[i].options.find(
        (ag) => ag.id === agentId
      );

      this.editForm.allowedAgents[i].value = {
        id: agentId,
        name: agent.name,
        number: agent.number,
        penalty: this.tierTimeoutEnabled ? value : 0,
      };
    },

    onTimeoutDestinationsSelection(destinations) {
      this.editForm.timeoutDestinations = [...destinations];
    },

    onQueueFullDestinationsSelection(destinations) {
      this.editForm.queueFullDestinations = [...destinations];
    },

    noAvailableAgentsDestinationsSelection(destinations) {
      this.editForm.noAvailableAgentsDestinations = [...destinations];
    },

    allAgentsBusyDestinationsSelection(destinations) {
      this.editForm.allAgentsBusyDestinations = [...destinations];
    },

    onExitKeyDestinationsSelection(destinations) {
      this.editForm.exitKeyDestinations = [...destinations];
    },

    onExtDestinationsSelect(destinations) {
      this.editForm.extDestinations = [...destinations];
    },

    clearFields() {
      this.defaultMsgNewMediafile = null;
      this.onAgentConnectNewMediafile = null;
      this.holdTimeNewMediafile = null;
      this.periodicAnnounceNewMediafile = null;
      this.draggableKey = "";
      this.selectedDids = [];
      this.initialNumber = null;
      this.activeTabName = "general";
      this.playToCaller = null;
      this.ringAgentsOnlyOnce = false;
      this.queueExitKey = false;
      this.tierTimeoutEnabled = false;
      Object.keys(this.editForm).forEach((key) => {
        if (typeof (this.editForm[key] === "boolean")) {
          this.editForm[key] = false;
        }

        if (typeof (this.editForm[key] === "string")) {
          this.editForm[key] = "";
        }

        if (Array.isArray(this.editForm[key])) {
          this.editForm[key] = [];
        }
      });

      this.editForm.tenantId = null;
      this.editForm.timeout = 120;
      this.editForm.callbackMaxAttempts = 1;
      this.editForm.agentConnectMeId = null;
      this.editForm.ivrId = null;
      this.editForm.periodicAnnounceFrequency = 10;
      this.editForm.periodicAnnounceMeId = null;
      this.editForm.announceHoldTime = "no";
      this.editForm.holdtimeMeId = null;
      this.editForm.announcePosition = "yes";
      this.editForm.announcePositionLimit = null;
      this.editForm.youAreNextMeId = null;
      this.editForm.thereAreMeId = null;
      this.editForm.callsWaitingMeId = null;
      this.editForm.thankYouMeId = null;
      this.editForm.announceFrequency = 0;
      this.editForm.agentTimeout = 30;
      this.editForm.agentWrapupTime = 0;
      this.editForm.allowedAgents = [
        {
          id: null,
          options: [],
          value: null,
        },
      ];
      this.editForm.loggedInAgents = [
        {
          _interface: null,
        },
      ];
      this.editForm.dids = [];
      this.editForm.enableAgentDestinations = false;
    },

    validateDestinations(destinations, type) {
      if (destinations.length > 1 && destinations.find((dst) => !dst.id)) {
        this.$notify({
          title: `Empty ${type} Destination field`,
          message: "Do not leave any destination field empty.",
          type: "error",
        });
        return;
      }

      let finalDestinations = [];
      if (destinations.length === 1 && destinations[0].id === null) {
        return finalDestinations;
      }

      if (destinations.length) {
        destinations.forEach((dst, idx) => (dst.order = idx + 1));

        finalDestinations = destinations.map(({ id, type, order }) => ({
          id,
          type,
          order,
        }));
      }

      return finalDestinations;
    },

    presentDestsModal() {
      const { userRole } = this.user;

      if (+userRole === 7) {
        this.editForm.destUpdateExts = [];
        this.destsModalVisible = true;
        return;
      }

      this.editOperation();
    },

    editOperation() {
      this.destsModalVisible = false;
      const editBody = { ...this.editForm };
      editBody.recording = editBody.recording ? "yes" : "no";
      editBody.nobodyHome = editBody.nobodyHome ? "on" : "";
      editBody.noFreeMember = editBody.noFreeMember ? "on" : "";
      editBody.notifyAbandoned = editBody.notifyAbandoned ? "on" : "";
      editBody.callback = editBody.callback ? "on" : "";
      editBody.reportHoldTime = editBody.reportHoldTime ? "yes" : "no";
      editBody.ringInUse = editBody.ringInUse ? "yes" : "no";
      editBody.autoPause = editBody.autoPause ? "yes" : "no";
      editBody.autofill = editBody.autofill ? "yes" : "no";
      editBody.autologout = editBody.autologout ? 1 : 0;
      editBody.agentPickup = editBody.agentPickup ? 1 : 0;
      editBody.branchId ? editBody.branchId : null;

      editBody.tenantId = this.selectedTenant.id;
      editBody.tenantCode = this.selectedTenant.tenantCode;
      editBody.exitKey = this.queueExitKey ? editBody.exitKey : false;
      editBody.periodicAnnounceFrequency = this.enablePeriodicAnnounce ? editBody.periodicAnnounceFrequency : 0;
      editBody.announceFrequency = editBody.announceHoldTime === 'no' && editBody.announcePosition === 'no' ? 0 : editBody.announceFrequency
      editBody.tierTimeout = this.tierTimeoutEnabled ? editBody.tierTimeout : null;


      // ⭐️ Route Call If No Agents Logged In (qu_nobodyhome) ⭐️ //
      if (editBody.nobodyHome === "") {
        editBody.noAvailableAgentsDestinations = [];
      }

      // ⭐️ Route Call If All Agents Busy (qu_nofreemembers) ⭐️ //
      if (editBody.noFreeMember === "") {
        editBody.allAgentsBusyDestinations = [];
      }

      // ⭐️ Notify Abandon Email (qu_notifyabandonedemail) ⭐️ //
      if (editBody.notifyAbandoned === "") {
        editBody.notifyAbandonedEmail = "";
      }

      // ⭐️ Call Back Caller Id Number (qu_callbackcalleridnum) ⭐️ //
      if (editBody.callback === "") {
        editBody.callbackCidNum = "";
      }

      // ⭐️ announce_position_limit ⭐️ //
      if (
        editBody.announcePosition === "yes" ||
        editBody.announcePosition === "no"
      ) {
        editBody.announcePositionLimit = 0;
      }

      if (editBody.ivrId === null) {
        editBody.ivrId = 0;
      }

      if (editBody.announcePositionLimit === null) {
        editBody.announcePositionLimit = 0;
      }

      // ⭐️ Handling qu_parameters ⭐️ //
      if (this.playToCaller === null) {
        this.$notify({
          title: "Invalid field",
          message: "Play to the caller is required!",
          type: "error",
        });
        return;
      } else {
        editBody.parameters = `${this.ringAgentsOnlyOnce ? "n" : ""}t${
          this.playToCaller
        }`;
      }

      if (editBody.announcePosition === "no") {
        editBody.youAreNextMeId = 0;
        editBody.thereAreMeId = 0;
        editBody.callsWaitingMeId = 0;
        editBody.thankYouMeId = 0;
      }

      editBody.periodicAnnounceMeId = editBody.periodicAnnounceMeId
        ? editBody.periodicAnnounceMeId
        : 0;
      editBody.holdtimeMeId = editBody.holdtimeMeId ? editBody.holdtimeMeId : 0;
      editBody.youAreNextMeId = editBody.youAreNextMeId
        ? editBody.youAreNextMeId
        : 0;
      editBody.thereAreMeId = editBody.thereAreMeId ? editBody.thereAreMeId : 0;
      editBody.callsWaitingMeId = editBody.callsWaitingMeId
        ? editBody.callsWaitingMeId
        : 0;
      editBody.thankYouMeId = editBody.thankYouMeId ? editBody.thankYouMeId : 0;

      // ⭐️ Allowed Agents ⭐️ //
      const allowedAgents = new Set();
      editBody.allowedAgents.forEach((ag) => {
        if (ag.id) {
          allowedAgents.add(ag.id);
        }
      });
      editBody.allowedAgents = [];
      allowedAgents.forEach((agId) => {
        const matchedAgent = this.editForm.allowedAgents.find(
          (agent) => agId === agent.id
        );

        matchedAgent.value.penalty = this.tierTimeoutEnabled ? matchedAgent.penalty : 0;
        if (matchedAgent) {
          editBody.allowedAgents.push(matchedAgent.value);
        }
      });

      const loggedInAgents = [];
      editBody.loggedInAgents.forEach((ag) => {
        if (
          !loggedInAgents.find((agent) => agent._interface === ag._interface) &&
          ag._interface !== null
        ) {
          loggedInAgents.push(ag);
        }
      });

      editBody.loggedInAgents = loggedInAgents;

      const timeoutDestinations = this.validateDestinations(
        editBody.timeoutDestinations,
        "TIMEOUT"
      );
      const queueFullDestinations = this.validateDestinations(
        editBody.queueFullDestinations,
        "QUEUE-FULL"
      );

      const noAvailableAgentsDestinations = this.validateDestinations(
        editBody.noAvailableAgentsDestinations,
        "NO-AVAILABLE-AGENT"
      );

      const allAgentsBusyDestinations = this.validateDestinations(
        editBody.allAgentsBusyDestinations,
        "ALL-AGENTS-BUSY"
      );

      const exitKeyDestinations = this.validateDestinations(
        editBody.exitKeyDestinations,
        "EXITKEY"
      );

      const extDestinations = this.validateDestinations(
        editBody.extDestinations,
        "EXTENSIONS"
      );

      if (
        timeoutDestinations &&
        queueFullDestinations &&
        noAvailableAgentsDestinations &&
        allAgentsBusyDestinations &&
        exitKeyDestinations &&
        extDestinations
      ) {
        editBody.timeoutDestinations = timeoutDestinations;
        editBody.queueFullDestinations = queueFullDestinations;
        editBody.noAvailableAgentsDestinations = noAvailableAgentsDestinations;
        editBody.allAgentsBusyDestinations = allAgentsBusyDestinations;
        editBody.exitKeyDestinations = exitKeyDestinations;
        editBody.extDestinations = extDestinations;
      } else {
        return;
      }

      this.$emit("editOperation", editBody);
    },

    cancelOperation() {
      this.$emit("cancelOperation", "edit");
    },

    remoteSearchForCidNums(query) {
      this.$emit("remoteSearchForCidNums", query);
    },

    async searchExtensions(query) {
      const searchTerm = (query + "")
        .replace(/[\\"']/g, "\\$&")
        .replace(/\u0000/g, "\\0");

      let queryTerm = {
        tenantId: this.selectedTenant.id,
        $limit: 25,
        $or: [
          { number: { $like: `%${searchTerm}%` } },
          { name: { $like: `%${searchTerm}%` } },
        ],
      };

      return await feathersClient.service("extensions").find({
        query: queryTerm,
      });
    },

    async remoteSearchExtensions(query, idx) {
      if (query && query.length) {
        const exts = await this.searchExtensions(query);
        this.editForm.allowedAgents[idx].options = [...exts];
      }
    },

    addAllowedAgent() {
      this.editForm.allowedAgents.push({
        options: [],
        id: null,
        value: null,
      });
    },

    removeAllowedAgent(idx) {
      this.editForm.allowedAgents.splice(idx, 1);

      if (!this.editForm.allowedAgents.length) {
        this.addAllowedAgent();
      }

      this.changeDraggableKey();
    },

    removeLoggedInAgent(idx) {
      if (this.editForm.loggedInAgents.length === 1) {
        this.editForm.loggedInAgents = [{ _interface: null }];
        return;
      }
      this.editForm.loggedInAgents.splice(idx, 1);
    },

    addLoggedInAgent() {
      this.editForm.loggedInAgents.push({ _interface: null });
    },
  },

  filters: {
    formatNumber(value) {
      return value
        ? value.replace(/(\d{3})(\d{3})(\d{4})/g, "($1) $2-$3")
        : value;
    },
  },

  created() {
    this.changeDraggableKey();
  },
};
</script>

<style lang="scss" scoped>
.el-tabs {
  max-width: 600px;
  margin: auto;
}

.el-checkbox {
  margin-left: 0;
}

.list-enter-active,
.list-leave-active,
.list-move {
  transition: 500ms cubic-bezier(0.59, 0.12, 0.34, 0.95);
  transition-property: opacity, transform;
}

.list-enter {
  opacity: 0;
  transform: translateX(50px) scaleY(0.5);
}

.list-enter-to {
  opacity: 1;
  transform: translateX(0) scaleY(1);
}

.list-leave-active {
  position: absolute;
}

.list-leave-to {
  opacity: 0;
  transform: scaleY(0);
  transform-origin: center top;
}
</style>
